import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Circle, Timer } from "lucide-react";
import { api, errMsg, fmtDate } from "../lib/api";
import { Crest, StatusBadge, DemoTag } from "./kit";
import { Button } from "./ui/button";
import { toast } from "sonner";

const parts = (s) => [Math.floor(s / 86400), Math.floor((s % 86400) / 3600), Math.floor((s % 3600) / 60), Math.floor(s % 60)];

function Countdown({ kickoff, skew }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const left = Math.max(0, (new Date(kickoff).getTime() - (now + skew)) / 1000);
  if (!kickoff) return <div className="text-sm font-semibold">Kickoff time not set</div>;
  if (left <= 0) return <div className="text-lg font-extrabold" data-testid="countdown-reached">Kickoff time reached — awaiting referee</div>;
  return (
    <div className="grid min-w-0 grid-cols-4 gap-2 lg:shrink-0" data-testid="countdown-timer">
      {parts(left).map((v, i) => (
        <div key={i} className="min-w-0 rounded-xl bg-[#17191C] px-2 py-2 text-center text-white sm:min-w-[64px] sm:px-3">
          <div className="font-mono text-2xl font-bold md:text-3xl" data-testid={`countdown-${["days", "hours", "minutes", "seconds"][i]}`}>{String(v).padStart(2, "0")}</div>
          <div className="text-[9px] font-bold uppercase tracking-widest text-white/60">{["Days", "Hrs", "Min", "Sec"][i]}</div>
        </div>
      ))}
    </div>
  );
}

export function NextMatchCountdown({ portal }) {
  const [m, setM] = useState(null);
  useEffect(() => { api.get(`/dashboard/${portal}`).then((r) => setM(r.data.next_prematch)).catch(() => {}); }, [portal]);
  return m ? <MatchDayCountdown match={m} /> : null;
}

export function MatchDayCountdown({ match: m }) {
  const [r, setR] = useState(null);
  const load = () => api.get(`/matches/${m.id}/readiness`).then((x) => setR({ ...x.data, skew: x.data.server_time * 1000 - Date.now() })).catch(() => {});
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [m.id]); // eslint-disable-line
  const confirmLeague = async () => {
    try {
      await api.put(`/matches/${m.id}/checklists/league`, { items: { "Registrations verified": true, "Administrative readiness confirmed": true } });
      toast.success("Administrative readiness confirmed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="tro-card overflow-hidden" data-testid="matchday-countdown">
      <div className="flex flex-col gap-4 border-b border-[#E6E9ED] p-5 lg:flex-row lg:items-center">
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2"><Timer className="h-4 w-4" /><span className="tro-label">Match Day Countdown</span><StatusBadge status={m.status} />{m.demo && <DemoTag />}</div>
          <Link to={`/matches/${m.id}`} className="mt-2 flex items-center gap-3 hover:underline" data-testid="countdown-match-link">
            <Crest club={m.home} size={36} /><span className="text-lg font-extrabold md:text-xl">{m.home.name} v {m.away.name}</span><Crest club={m.away} size={36} />
          </Link>
          <div className="mt-1 text-xs text-[#6C757D]">{m.competition?.name} · {m.match_number} · {m.venue} · {fmtDate(m.kickoff)}</div>
        </div>
        <Countdown kickoff={r?.kickoff || m.kickoff} skew={r?.skew || 0} />
      </div>
      {r && (
        <div className="p-5">
          <div className="mb-3 flex items-center gap-3">
            <span className="tro-label">Readiness</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-[#E6E9ED]"><div className="h-full bg-[#1E7E34] transition-[width] duration-500" style={{ width: `${r.ready_pct}%` }} /></div>
            <b className="font-mono text-sm" data-testid="readiness-pct">{r.ready_pct}%</b>
          </div>
          <ul className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3" data-testid="readiness-list">
            {r.items.map((i) => (
              <li key={i.key} className="flex items-start gap-2 rounded-lg bg-[#F7F8F9] p-2.5" data-testid={`readiness-${i.key}`}>
                {i.done ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#1E7E34]" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 text-[#C5CBD1]" />}
                <div className="min-w-0"><div className="text-sm font-bold">{i.label}</div><div className="text-xs text-[#6C757D]">{i.detail} · {i.owner}</div></div>
              </li>
            ))}
          </ul>
          {r.can_confirm_league && !r.items.find((i) => i.key === "league_checklist")?.done && (
            <Button className="mt-3" size="sm" data-testid="confirm-league-readiness-btn" onClick={confirmLeague}>Confirm administrative readiness</Button>
          )}
        </div>
      )}
    </div>
  );
}
